import { Box } from "@mui/material";
import { NULL_DISPLAY } from "@platform/design-system";

import type { CellPair } from "@platform/formatting-utils/financials";

type FormatLeg = (value: number) => string;

const legColor = (value: number): string => {
  if (value < 0) return "success.main";
  if (value > 0) return "error.main";

  return "text.primary";
};

interface CellPairDisplayProps {
  pair: CellPair;
  formatLeg: FormatLeg;
  stacked?: boolean;
}

const SingleLeg = ({
  value,
  formatLeg,
}: {
  value: number;
  formatLeg: FormatLeg;
}): React.JSX.Element => (
  <Box component="span" sx={{ color: legColor(value), whiteSpace: "nowrap" }}>
    {formatLeg(value)}
  </Box>
);

export const CellPairDisplay = ({
  pair,
  formatLeg,
  stacked = false,
}: CellPairDisplayProps): React.JSX.Element => {
  const { toCurrent, toOther } = pair;

  if (toCurrent === null && toOther === null) return <>{NULL_DISPLAY}</>;

  if (toCurrent !== null && toOther !== null) {
    if (stacked) {
      return (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <SingleLeg value={toCurrent} formatLeg={formatLeg} />
          <SingleLeg value={toOther} formatLeg={formatLeg} />
        </Box>
      );
    }

    return (
      <Box
        component="span"
        sx={{ display: "inline-flex", gap: 0.5, whiteSpace: "nowrap" }}
      >
        <SingleLeg value={toCurrent} formatLeg={formatLeg} />
        <Box component="span" sx={{ color: "text.secondary" }}>
          |
        </Box>
        <SingleLeg value={toOther} formatLeg={formatLeg} />
      </Box>
    );
  }

  const value = toCurrent ?? toOther!;

  return <SingleLeg value={value} formatLeg={formatLeg} />;
};
